import { useState, useEffect } from "react";
import { Star, Check, Trash2, MessageSquare } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const AdminTestimonials = () => {
  const [pending, setPending] = useState([]);
  const [whatsappClicks, setWhatsappClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("reviews");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rev, wa] = await Promise.all([
        adminApi.get("/admin/testimonials/pending"),
        adminApi.get("/admin/whatsapp-clicks"),
      ]);
      setPending(rev.data);
      setWhatsappClicks(wa.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id) => {
    try {
      await adminApi.patch(`/admin/testimonials/${id}/approve`);
      setPending((p) => p.filter((t) => t.id !== id));
      toast.success("Review approved and published");
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await adminApi.delete(`/admin/testimonials/${id}`);
      setPending((p) => p.filter((t) => t.id !== id));
      toast.success("Review deleted");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-testimonials-page">
        <h1 className="text-3xl font-serif mb-2">Reviews & Leads</h1>
        <p className="text-muted-foreground mb-6">Approve customer reviews and track WhatsApp lead activity.</p>

        <div className="flex gap-2 mb-6 border-b">
          <button
            data-testid="tab-reviews"
            onClick={() => setTab("reviews")}
            className={`px-4 py-2 -mb-px border-b-2 transition-colors ${tab === "reviews" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground"}`}
          >
            Pending Reviews ({pending.length})
          </button>
          <button
            data-testid="tab-whatsapp"
            onClick={() => setTab("whatsapp")}
            className={`px-4 py-2 -mb-px border-b-2 transition-colors ${tab === "whatsapp" ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground"}`}
          >
            WhatsApp Clicks ({whatsappClicks.length})
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner"></div></div>
        ) : tab === "reviews" ? (
          pending.length === 0 ? (
            <div className="bg-white rounded-lg p-12 text-center text-muted-foreground" data-testid="no-pending-reviews">
              No pending reviews. Approved reviews appear on the homepage automatically.
            </div>
          ) : (
            <div className="space-y-4" data-testid="pending-reviews-list">
              {pending.map((t) => (
                <div key={t.id} data-testid={`pending-${t.id}`} className="bg-white rounded-lg p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="font-semibold text-primary">{t.customer_name}</div>
                        <div className="flex">
                          {[...Array(t.rating)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">{t.package_name}</span>
                      </div>
                      <p className="text-muted-foreground italic">"{t.review}"</p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Submitted {new Date(t.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        {t.submitted_email && <span> · {t.submitted_email}</span>}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button size="sm" onClick={() => approve(t.id)} data-testid={`approve-${t.id}`} className="bg-green-600 hover:bg-green-700 gap-1">
                        <Check size={14} /> Approve
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(t.id)} data-testid={`delete-${t.id}`} className="gap-1 text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 size={14} /> Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : whatsappClicks.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center text-muted-foreground" data-testid="no-whatsapp-clicks">
            No WhatsApp clicks logged yet.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-testid="whatsapp-clicks-list">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left py-3 px-4">When</th>
                  <th className="text-left py-3 px-4">Page</th>
                  <th className="text-left py-3 px-4">Package</th>
                  <th className="text-left py-3 px-4">Context</th>
                </tr>
              </thead>
              <tbody>
                {whatsappClicks.map((c) => (
                  <tr key={c.id} className="border-b hover:bg-muted/30">
                    <td className="py-3 px-4">{new Date(c.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-3 px-4"><code className="text-xs">{c.page_url || "—"}</code></td>
                    <td className="py-3 px-4">{c.package_name || "—"}</td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                        <MessageSquare size={10} /> {c.context}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTestimonials;
